import { useEffect, useRef, useState } from 'react';
import { uploadFile, uploadFailureMessage, getFileDownloadUrl, type FilePurpose } from '../../api/file';
import { extractDocument, type ExtractionDocumentType, type ExtractionJob } from '../../api/aiExtraction';
import { shrinkDocumentImage } from '../../utils/imageResize';
import { ExtractionResult } from './ExtractionResult';

type SlotStatus = 'idle' | 'uploading' | 'done' | 'error';

/** Fills a form from what AI read. Without `overwrite`, only inputs that are
 * still empty are filled — never something the user already typed. */
export type ApplyExtracted = (fields: Record<string, unknown>, opts: { overwrite: boolean }) => void;

interface Preview {
  url: string;
  name?: string;
  /** False for a picked PDF; unknown (undefined) for an already-stored file,
   * which is tried as an image and falls back to a file link if it isn't one. */
  isImage?: boolean;
  /** Local object URLs are revoked when replaced; server URLs are not ours to revoke. */
  local: boolean;
}

/**
 * One reusable document control. A picked file is shown straight away from a
 * local object URL while the upload runs behind it, and an already-stored file
 * is shown as a thumbnail too — so the user always sees what is attached.
 *
 * AI extraction runs only when the caller wants the form filled from the
 * document (`onExtracted`). It starts once the upload lands, fills the empty
 * inputs by itself when the reading arrives, and shows what was read with the
 * backend's checks. A plain document checklist passes no `onExtracted` and
 * gets no AI step at all. Nothing here blocks the surrounding form.
 */
export function DocumentUploadSlot({
  label,
  docType,
  purpose,
  existingFileId,
  onUploaded,
  onExtracted,
  hint,
  accept = 'image/*,application/pdf',
}: {
  label: string;
  docType: ExtractionDocumentType;
  purpose: FilePurpose;
  existingFileId?: string;
  /** Also handed the picked file, for callers that list it as an attachment. */
  onUploaded?: (fileId: string, file: File) => void;
  onExtracted?: ApplyExtracted;
  hint?: string;
  /** File types offered by the picker; images and PDF unless the caller reads more (a contract also takes .docx). */
  accept?: string;
}) {
  const [status, setStatus] = useState<SlotStatus>(existingFileId ? 'done' : 'idle');
  const [reading, setReading] = useState(false);
  const [job, setJob] = useState<ExtractionJob | null>(null);
  // A slower reading of an earlier pick must not fill the form after a re-pick.
  const pickSeq = useRef(0);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewBroken, setPreviewBroken] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const mountedRef = useRef(true);
  // Set back to true on (re)mount: StrictMode mounts, unmounts and remounts
  // every component in dev, so a cleanup-only effect left this false for good
  // and every finished upload was thrown away with the slot stuck on
  // "Đang tải lên...".
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // A stored file has no local copy — resolve a download URL for its thumbnail.
  // Skipped while a freshly picked file is already on screen.
  useEffect(() => {
    if (!existingFileId) return;
    let cancelled = false;
    getFileDownloadUrl(existingFileId)
      .then((url) => {
        if (cancelled) return;
        setPreview((p) => (p?.local ? p : { url, local: false }));
        setPreviewBroken(false);
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [existingFileId]);

  useEffect(() => {
    if (!preview?.local) return;
    return () => URL.revokeObjectURL(preview.url);
  }, [preview]);

  async function handleFile(file: File) {
    const seq = ++pickSeq.current;
    setPreview({ url: URL.createObjectURL(file), name: file.name, isImage: file.type.startsWith('image/'), local: true });
    setPreviewBroken(false);
    setStatus('uploading');
    setError(null);
    setJob(null);
    let fileId: string;
    try {
      const body = await shrinkDocumentImage(file);
      fileId = await uploadFile(body, purpose, body.type || file.type || 'application/octet-stream');
    } catch (err) {
      if (!mountedRef.current || seq !== pickSeq.current) return;
      setStatus('error');
      setError(uploadFailureMessage(err, label));
      return;
    }
    if (!mountedRef.current || seq !== pickSeq.current) return;
    onUploaded?.(fileId, file);
    setStatus('done');
    if (!onExtracted) return;
    // The file is stored by now; a reading failure is reported as such, never
    // as a failed upload.
    setReading(true);
    const result = await extractDocument(fileId, docType);
    if (!mountedRef.current || seq !== pickSeq.current) return;
    setReading(false);
    setJob(result);
    if (result.status === 'DONE' && result.fields) onExtracted(result.fields, { overwrite: false });
  }

  const showImage = preview && preview.isImage !== false && !previewBroken;

  return (
    <div className={`rounded-xl border bg-white p-3 ${status === 'error' ? 'border-red-300' : 'border-slate-200'}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-slate-700">{label}</p>
        {status === 'idle' && <span className="text-[10px] text-slate-400">Chưa có</span>}
        {status === 'uploading' && <span className="text-[10px] text-sky-600 animate-pulse">Đang tải lên...</span>}
        {status === 'done' && <span className="text-[10px] text-agrida-600">✓ Đã có</span>}
        {status === 'error' && <span className="text-[10px] text-red-600">Tải lên lỗi</span>}
      </div>
      {hint && <p className="mt-0.5 text-[10px] text-slate-400">{hint}</p>}

      {preview && (
        <a
          href={preview.url}
          target="_blank"
          rel="noopener noreferrer"
          title="Xem tệp lớn"
          className="mt-2 block overflow-hidden rounded-lg border border-slate-200 bg-slate-50"
        >
          {showImage ? (
            <img
              src={preview.url}
              alt={label}
              onError={() => setPreviewBroken(true)}
              className={`h-40 w-full object-contain ${status === 'uploading' ? 'opacity-60' : ''}`}
            />
          ) : (
            <div className="flex h-14 items-center gap-2 px-3">
              <i className="ri-file-text-line text-2xl text-slate-500" />
              <span className="truncate text-xs text-slate-700">{preview.name || 'Xem tệp đã lưu'}</span>
            </div>
          )}
        </a>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Cleared so picking the same file again after an error still fires onChange.
          e.target.value = '';
          if (file) void handleFile(file);
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={status === 'uploading'}
        className="mt-2 w-full h-8 rounded-lg border border-dashed border-agrida-300 bg-agrida-50 text-xs font-semibold text-agrida-700 disabled:opacity-60"
      >
        {preview || existingFileId ? 'Tải lên tệp khác' : 'Chọn ảnh / tệp'}
      </button>

      {status === 'error' && <p className="mt-1.5 text-[11px] text-red-600">{error}</p>}

      {onExtracted && (
        <ExtractionResult
          docType={docType}
          reading={reading}
          job={job}
          onOverwrite={job?.fields ? () => onExtracted(job.fields!, { overwrite: true }) : undefined}
        />
      )}
    </div>
  );
}
